import { STATEMENT_ARTIFACT_BUCKET } from "@/lib/statement-artifact";
import { eventStartMillis } from "@/lib/event-timing";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase as typedSupabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { createMessageRecords, type MessageInsert } from "@/api/messages";
import { toast } from "sonner";
import { ensureFutureChargeDate, type RecurringFrequency } from "@/lib/recurring-gifts";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import {
  localDateYmd,
  parseLocalDate,
  safeFormatDate,
  timeZoneDateYmd,
  parseEventDateTime,
  zonedMidnightToUtcIso,
  zonedEndOfDayToUtcIso,
} from "@/lib/date-utils";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useOrgMidnightTick } from "@/hooks/useMidnightTick";
import { loadStaffGivingStatementPdf } from "@/lib/pdf-runtime-loaders";
import {
  buildCanonicalRequestInsert,
} from "@/lib/request-schema-compat";
import { logAudit } from "@/lib/audit";
import { notifyRequestCreated } from "@/lib/request-notifications";
import { computeDisabledSectionTypes } from "@/lib/bulletin-section-enabled";
import type { BulletinSectionKey } from "@/components/bulletins/bulletin-data";
import { summarizeContributionAllocations } from "@/lib/contribution-allocation-display";
import {
  isCountableGivingContribution,
  isCountableGivingContributionRecord,
} from "@/lib/contribution-settlement";
import { fetchAllRows } from "@/lib/paged-select";
import { clampStatementEndDate, getStatementYear } from "@/lib/statement-date-range";

// This hook still touches tables and RPCs missing from the generated schema.
// Keep the escape hatch local instead of widening the shared client type.
const supabase = typedSupabase as unknown as {
  from: (table: string) => any;
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: any; error: { message?: string } | null }>;
  storage: typeof typedSupabase.storage;
};

const unavailableMemberPledgesOrgs = new Set<string>();
const isMemberPledgesUnavailable = (orgId: string | null | undefined) =>
  !!orgId && unavailableMemberPledgesOrgs.has(orgId);
const markMemberPledgesUnavailable = (orgId: string | null | undefined) => {
  if (orgId) unavailableMemberPledgesOrgs.add(orgId);
};

export const isCountableMemberGivingContribution = (
  status: string | null | undefined,
  batchSource: string | null | undefined,
) => isCountableGivingContribution(status, batchSource);

const normalizePortalRequest = (row: Record<string, unknown>) => ({
  ...row,
  title: (row.summary as string) || "Request",
  description: (row.details as string | null) ?? null,
  type: (row.category as string) || "other",
  person_id: (row.requester_person_id as string | null) || null,
});

export type MemberGivingFundCatalogEntry = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  type: string;
  is_active: boolean;
  online_giving_enabled: boolean;
  is_tax_deductible: boolean;
};

export const getMemberGivingFundCatalog = async (organizationId: string) => {
  return fetchAllRows((from, to) =>
    typedSupabase
      .rpc("get_member_giving_fund_catalog", { p_organization_id: organizationId })
      .order("id", { ascending: true })
      .range(from, to)
  ) as Promise<MemberGivingFundCatalogEntry[]>;
};

export type MemberGivingAllocationEntry = {
  contribution_id: string;
  fund_id: string;
  amount: number;
  non_deductible_amount: number;
  allocation_id: string;
};

export const getMemberGivingAllocations = async (
  organizationId: string,
  personId: string,
  // Optional bound to a specific set of contribution ids. get_member_giving_allocations
  // has no such parameter itself, but it returns a TABLE(...), so PostgREST accepts an
  // ordinary post-filter on top of it — pushing the filter down to Postgres instead of
  // paging through every allocation the person has ever had. Callers that need the full
  // history (useMemberGivingTransactions) omit this; a bounded preview should not.
  contributionIds?: string[],
) => {
  return fetchAllRows((from, to) => {
    let query = typedSupabase.rpc("get_member_giving_allocations", {
      p_organization_id: organizationId,
      p_person_id: personId,
    });
    if (contributionIds) {
      query = query.in("contribution_id", contributionIds);
    }
    return query
      .order("contribution_id", { ascending: true })
      .order("fund_id", { ascending: true })
      .order("amount", { ascending: true })
      .order("non_deductible_amount", { ascending: true })
      .order("allocation_id", { ascending: true })
      .range(from, to);
  }) as Promise<MemberGivingAllocationEntry[]>;
};

const addMemberGivingAllocations = <T extends { id: string }>(
  contributions: T[],
  catalog: MemberGivingFundCatalogEntry[],
  allocations: MemberGivingAllocationEntry[],
) => {
  const fundsById = new Map(catalog.map((fund) => [fund.id, fund]));
  const allocationsByContributionId = new Map<string, MemberContribution["allocations"]>();

  for (const allocation of allocations) {
    const contributionAllocations = allocationsByContributionId.get(allocation.contribution_id) ?? [];
    contributionAllocations.push({
      amount: allocation.amount,
      non_deductible_amount: allocation.non_deductible_amount,
      fund: fundsById.get(allocation.fund_id) ?? null,
    });
    allocationsByContributionId.set(allocation.contribution_id, contributionAllocations);
  }

  return contributions.map((contribution) => ({
    ...contribution,
    allocations: allocationsByContributionId.get(contribution.id) ?? [],
  }));
};

const mapMemberGivingFundNames = <T extends { fund_id?: string | null }>(
  rows: T[],
  catalog: MemberGivingFundCatalogEntry[],
) => {
  const fundsById = new Map(catalog.map((fund) => [fund.id, fund]));
  return rows.map((row) => ({
    ...row,
    fund: row.fund_id ? fundsById.get(row.fund_id) ?? null : null,
  }));
};

// These types are manually defined since they may not exist in the generated schema
export interface MemberPortalSettings {
  id: string;
  organization_id: string;
  portal_enabled: boolean;
  welcome_title: string | null;
  welcome_message: string | null;
  allow_giving_statements: boolean;
  allow_requests: boolean;
  allow_notifications: boolean;
  allow_group_discovery: boolean;
  checkin_enabled: boolean;
  checkin_geofence_miles: number | null;
  directory_enabled: boolean;
  member_home_cards: string[] | null;
  created_at: string;
  updated_at: string;
}

/**
 * Group-level discovery controls which groups members may browse. The optional
 * portal-wide setting remains an explicit kill switch, but older organizations
 * without a settings row must not silently lose their discoverable groups.
 */
export function isGroupDiscoveryEnabled(
  settings: Pick<MemberPortalSettings, "allow_group_discovery"> | null | undefined,
): boolean {
  // `null` is the successful "no settings row" result. `undefined`, on the
  // other hand, is React Query's unresolved/error state and must not bypass an
  // organization's persisted kill switch while settings are unknown.
  if (settings === null) return true;
  return settings?.allow_group_discovery === true;
}

export interface MemberGivingReadiness {
  organization_name: string;
  organization_slug: string;
  payments_ready: boolean;
  enabled_fund_count: number;
}

export function useMemberGivingReadiness() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-giving-readiness", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase.rpc(
        "get_mobile_giving_readiness" as never,
        { p_organization_id: organizationId } as never,
      );

      if (error) throw error;
      return ((data as MemberGivingReadiness[] | null) ?? [])[0] ?? null;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

export interface NotificationPreference {
  id: string;
  organization_id: string;
  person_id: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  digest_frequency: string | null;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  category_preferences: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface MemberNotification {
  id: string;
  organization_id: string;
  person_id: string;
  title: string;
  body: string | null;
  category: string | null;
  is_read: boolean;
  read_at: string | null;
  link_url: string | null;
  link_label: string | null;
  created_at: string;
}

export interface MemberAnnouncement {
  id: string;
  organization_id: string;
  title: string;
  summary?: string | null;
  body?: string | null;
  content?: string | null;
  status?: string | null;
  published_at?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  expires_at?: string | null;
  pinned_until?: string | null;
  created_at: string;
  pinned: boolean;
  is_pinned?: boolean;
}

export interface MemberBulletin {
  id: string;
  organization_id: string;
  title: string;
  content?: string | null;
  status?: string | null;
  published_at?: string | null;
  is_published?: boolean | null;
  week_of?: string | null;
  created_at: string;
}

export interface MemberRequest {
  id: string;
  organization_id: string;
  person_id: string;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  group_id?: string | null;
}

export interface GivingStatement {
  id: string;
  organization_id: string;
  person_id: string;
  year: number;
  file_url: string | null;
  created_at: string;
  statement_year?: number;
  total_amount?: number;
  statement_url?: string | null;
  artifact_bucket?: string | null;
  artifact_path?: string | null;
}

export type MemberPortalRequest = Tables<"requests">;

type NotificationInsert = Record<string, unknown>;
type RequestInsert = TablesInsert<"requests">;
type MemberRequestInsert = Record<string, unknown>;
type EventRegistrationInsert = TablesInsert<"event_registrations">;

export interface MemberContribution {
  id: string;
  amount: number;
  date: string;
  method: string;
  status: string;
  batch_source?: string | null;
  deleted_at?: string | null;
  void_reason?: string | null;
  allocations?: {
    amount: number;
    non_deductible_amount?: number | null;
    fund?: {
      id: string;
      name: string;
      is_tax_deductible?: boolean;
    } | null;
  }[];
}

export interface MemberGivingSummary {
  ytdTotal: number;
  lastYearTotal: number;
  lastGiftDate: string | null;
}

export interface MemberRecurringGift {
  id: string;
  organization_id: string;
  person_id: string;
  fund_id: string;
  amount: number;
  frequency: RecurringFrequency;
  start_date: string;
  next_charge_date: string;
  status: string;
  paused_at: string | null;
  canceled_at: string | null;
  skip_next_charge?: boolean;
  created_at: string;
  updated_at: string;
  fund?: {
    id: string;
    name: string;
  } | null;
}

/**
 * One row of the member_pledge_progress view. Mirrors the view's real columns:
 * this type used to also declare `id`, `fulfilled_amount` and a required
 * `frequency` that the view never returned, so reads of them compiled and then
 * quietly produced undefined/NaN on screen (#6543). Use `pledge_id` and
 * `total_received`.
 */
export interface MemberPledgeProgress {
  pledge_id: string;
  organization_id: string;
  person_id: string;
  campaign_name: string;
  total_amount: number;
  installment_amount: number | null;
  start_date: string;
  end_date: string | null;
  status: string;
  total_received: number;
  /** Pledge cadence. Absent until the view migration that adds it is deployed. */
  frequency?: string | null;
  /** Explicitly recorded number of payments. Absent until the view migration that adds it is deployed. */
  installment_count?: number | null;
  /** Fund backing the pledge — differentiates identically-titled campaigns. */
  fund_name?: string | null;
  /** Record creation timestamp — unique per record when fund+years collide. */
  created_at?: string | null;
}

export interface MemberPledgeCampaign {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  start_date: string;
  end_date: string | null;
  fund_id: string | null;
}

export interface MemberEventRegistration {
  id: string;
  organization_id: string;
  event_id: string;
  person_id: string;
  status: string;
  payment_status: string;
  contribution_id: string | null;
  notes: string | null;
  answers: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  event?: {
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    all_day: boolean;
    all_day_date?: string | null;
    all_day_end_date?: string | null;
    location: string | null;
    registration_enabled: boolean | null;
    registration_capacity: number | null;
    registration_fee: number | null;
  } | null;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
  } | null;
}

export interface MemberAttendanceRecord {
  id: string;
  organization_id: string;
  person_id: string;
  event_id: string | null;
  event_name: string;
  event_date: string;
  check_in_method: string;
  check_in_time: string | null;
  notes: string | null;
  event?: {
    id: string;
    title: string;
    start_time: string;
    location: string | null;
  } | null;
}

export interface MemberGroup {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_open: boolean;
  is_discoverable: boolean;
}

export interface MemberGroupMembership {
  id: string;
  group_id: string;
  person_id: string;
  organization_id: string;
  role: "member" | "leader";
  group: MemberGroup;
}

export interface GroupRosterEntry {
  id: string;
  role: "member" | "leader";
  person: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    photo_url: string | null;
    households?: {
      id: string;
      name: string;
      address: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
    } | null;
  };
  privacy: {
    share_phone: boolean;
    share_address: boolean;
    share_birthday: boolean;
  } | null;
}

/**
 * What `useGroupRoster` returns: the entries the caller may read, plus how many
 * co-members they cannot act on — whether RLS nulled the person embed or the
 * server truncated the response at max_rows (1000).
 *
 * `hiddenCount > 0` means **the roster is incomplete** and any roster-wide
 * action — saving attendance, messaging the whole group, scheduling meeting
 * reminders — would silently operate on a subset. For a non-staff group leader
 * that subset can be their own row alone. Callers must surface the shortfall
 * and refuse those actions rather than run them against a partial list
 * (Codex P1 on PR #6092).
 */
export interface GroupRosterResult {
  entries: GroupRosterEntry[];
  /**
   * Group members `entries` does not contain, from both causes: rows the
   * server never sent (max_rows truncation) and rows whose `person` embed RLS
   * nulled. When `countExact` is false this is only a lower bound — the
   * truncation half is invisible — so it must never be read as "the rest are
   * visible".
   */
  hiddenCount: number;
  /**
   * Of `hiddenCount`, the rows the server DID send but whose `person` embed RLS
   * nulled. This is the only cause an administrator can do anything about.
   */
  hiddenByPermission: number;
  /**
   * Of `hiddenCount`, the rows the server never sent because the group exceeds
   * PostgREST's max_rows and this query does not paginate. An administrator
   * running the same query is truncated identically, so this must never be
   * described as a permission problem or answered with "ask an administrator"
   * (Codex P2 round 8 on #6092).
   */
  hiddenByTruncation: number;
  /**
   * Of `hiddenLeaderCount`, the leader rows RLS withheld specifically. Their
   * `role` was readable even though their `person` was not, so this is exact —
   * unlike the truncated remainder, whose roles are unknown. Lets leader-facing
   * copy say "not visible to you" only when that is literally what happened
   * (Codex P2 round 9 on #6092).
   */
  hiddenLeadersByPermission: number;
  /**
   * How many of the hidden rows are — or could be — leaders. `group_members.role`
   * is readable even when the `people` embed is not, so an RLS-nulled row's role
   * is known exactly; a row the server never sent carries no role at all and is
   * counted here as a possible leader. Lets a leaders-only action stay available
   * when every leader is visible and only ordinary members are hidden
   * (Codex P2 round 7 on #6092).
   */
  hiddenLeaderCount: number;
  /**
   * False when the response carried no exact count. `count` is nullable and is
   * the only thing that makes max_rows truncation visible, so its absence means
   * completeness is UNKNOWN — never "nothing is hidden" (Codex P2 round 7 on
   * #6092). Callers must treat false as incomplete regardless of the counts.
   */
  countExact: boolean;
}

export interface GroupJoinRequest extends MemberRequest {
  person: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
    photo_url: string | null;
  } | null;
}

export interface GroupMeeting {
  id: string;
  organization_id: string;
  group_id: string;
  title: string | null;
  meeting_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  description: string | null;
}

export interface GroupAttendance {
  id: string;
  organization_id: string;
  group_id: string;
  person_id: string;
  meeting_date: string;
  present: boolean;
}

export interface GroupMaterial {
  id: string;
  organization_id: string;
  group_id: string;
  title: string | null;
  description: string | null;
  url: string | null;
  file_path: string | null;
  file_name: string | null;
  mime_type: string | null;
  created_by: string | null;
  created_at: string;
}

interface GroupReminderPreferences {
  person_id: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  category_preferences: Record<string, unknown> | null;
}

export interface MemberNextUpItem {
  type: "event" | "volunteer";
  title: string;
  startTime: string;
  endTime?: string;
  allDay?: boolean;
  allDayDate?: string | null;
  allDayEndDate?: string | null;
  location?: string | null;
  href?: string;
}

export interface MemberServingAssignment {
  id: string;
  status?: "pending" | "confirmed" | "declined" | "no_show" | null;
  position?: {
    title?: string | null;
    name?: string | null;
  } | null;
  service: {
    id: string;
    title: string | null;
    scheduled_date: string;
    start_time: string | null;
    end_time: string | null;
    location: string | null;
  };
}

const DEFAULT_SERVICE_SORT_TIME = "23:59:59";

const serviceStartTimestamp = (
  service: {
    scheduled_date?: string | null;
    start_time?: string | null;
  } | null | undefined,
) => {
  if (!service?.scheduled_date) return null;
  return `${service.scheduled_date}T${service.start_time ?? DEFAULT_SERVICE_SORT_TIME}`;
};

const serviceStartIso = (
  service: {
    scheduled_date?: string | null;
    start_time?: string | null;
  } | null | undefined,
) => {
  const timestamp = serviceStartTimestamp(service);
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const compareServingAssignments = (
  a: MemberServingAssignment,
  b: MemberServingAssignment,
) => {
  const aStart = serviceStartTimestamp(a.service) ?? "";
  const bStart = serviceStartTimestamp(b.service) ?? "";
  if (aStart !== bStart) return aStart.localeCompare(bStart);
  return a.id.localeCompare(b.id);
};

export interface MemberDirectoryEntry {
  person_id: string;
  display_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  photo_url: string | null;
  share_phone: boolean;
  share_address: boolean;
  share_birthday: boolean;
  first_name: string | null;
  last_name: string | null;
  birthday: string | null;
  household_id: string | null;
  household_name: string | null;
  household_address: string | null;
  household_city: string | null;
  household_state: string | null;
  household_zip: string | null;
  ministries?: { id: string; name: string }[];
}

// Privacy flags stored on directory profile rows
export interface DirectoryShareFlags {
  // #4970: explicit, default-false opt-in for directory listing itself --
  // auto_create_directory_profile() still creates the row unconditionally,
  // so row presence alone never signals consent. Grant-direction writes are
  // guarded server-side by the enforce_directory_listing_self_only trigger;
  // only the linked member can flip this true for their own row.
  is_listed: boolean;
  share_phone: boolean;
  share_address: boolean;
  share_birthday: boolean;
  // #5004: explicit, default-false opt-in for peer-message discoverability
  // -- deliberately separate from directory_profiles row presence, which
  // auto_create_directory_profile() creates unconditionally and so cannot
  // itself signal genuine consent (#4970).
  peer_messaging_opt_in: boolean;
}

interface CreateGroupMaterialInput {
  groupId: string;
  title?: string | null;
  description?: string | null;
  url?: string | null;
  file?: File | null;
}

export function useMemberPortalSettings() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-portal-settings", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("member_portal_settings")
        .select("*")
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as MemberPortalSettings | null;
    },
    enabled: !!organizationId,
  });
}

export function useUpsertMemberPortalSettings() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Partial<MemberPortalSettings>) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from("member_portal_settings")
        .upsert({
          organization_id: organizationId,
          portal_enabled: input.portal_enabled ?? true,
          welcome_title: input.welcome_title ?? null,
          welcome_message: input.welcome_message ?? null,
          allow_giving_statements: input.allow_giving_statements ?? true,
          allow_requests: input.allow_requests ?? true,
          allow_notifications: input.allow_notifications ?? true,
          allow_group_discovery: input.allow_group_discovery ?? false,
          checkin_enabled: input.checkin_enabled ?? false,
          checkin_geofence_miles: input.checkin_geofence_miles ?? null,
          directory_enabled: input.directory_enabled ?? true,
          member_home_cards: input.member_home_cards ?? null,
          updated_at: new Date().toISOString(),
        }, { onConflict: "organization_id" })
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;
      return data as MemberPortalSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-portal-settings", organizationId] });
      toast.success("Member portal settings saved");
    },
    onError: (error) => {
      console.error("Failed to save member portal settings:", error);
      toast.error("Failed to save member portal settings");
    },
  });
}

export function useDirectoryShareFlags(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery<DirectoryShareFlags | null>({
    queryKey: ["directory-share-flags", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return null;

      // peer_messaging_opt_in isn't in the generated Database type yet (new
      // column, types not regenerated -- follows this repo's existing
      // precedent of `as never`/casting for schema additions ahead of a
      // full type regen, e.g. get_my_coordinator_logged_service_hours).
      const { data, error } = await supabase
        .from("directory_profiles")
        .select("is_listed, share_phone, share_address, share_birthday, peer_messaging_opt_in" as never)
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as unknown as DirectoryShareFlags | null;
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useUpdateDirectoryShareFlags() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      personId: string;
      share_phone: boolean;
      share_address: boolean;
      share_birthday: boolean;
    }) => {
      if (!organizationId) throw new Error("No organization");

      const { error } = await supabase
        .from("directory_profiles")
        .update({
          share_phone: params.share_phone,
          share_address: params.share_address,
          share_birthday: params.share_birthday,
        })
        .eq("organization_id", organizationId)
        .eq("person_id", params.personId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directory-share-flags"] });
      queryClient.invalidateQueries({ queryKey: ["member-directory-entries"] });
    },
  });
}

// #4970: unlike peer_messaging_opt_in, is_listed is NOT column-REVOKEd --
// a plain UPDATE (same shape as useUpdateDirectoryShareFlags above) would be
// enough for the row-exists case, because the enforce_directory_listing_self_only
// trigger (20260815120000_restore_directory_listing_opt_in.sql) already blocks
// any non-owner from setting it true regardless of RLS/column grants; only the
// revoke direction needs to stay open for staff/admin moderation.
//
// PR #5315 round-1 Codex finding (P2): a member who opted out via
// WelcomeWizard (which used to delete the row entirely) had no
// directory_profiles row left, so a plain UPDATE here would silently affect
// zero rows and MemberPreferences would never let them opt back in. Upsert
// instead, so this can recreate a missing row -- the same
// (organization_id, person_id) unique constraint and column set
// useUpsertDirectoryProfile already relies on for the WelcomeWizard path.
export function useUpdateDirectoryListing() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      personId: string;
      isListed: boolean;
      displayName: string;
      contactEmail?: string | null;
      contactPhone?: string | null;
      photoUrl?: string | null;
    }) => {
      if (!organizationId) throw new Error("No organization");

      const { error } = await supabase
        .from("directory_profiles")
        .upsert(
          {
            organization_id: organizationId,
            person_id: params.personId,
            display_name: params.displayName,
            contact_email: params.contactEmail ?? null,
            contact_phone: params.contactPhone ?? null,
            photo_url: params.photoUrl ?? null,
            is_listed: params.isListed,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "organization_id,person_id" },
        )
        .eq("organization_id", organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directory-share-flags"] });
      queryClient.invalidateQueries({ queryKey: ["member-directory-entries"] });
    },
  });
}

// #5004 round-3 Codex finding (P1): directory_profiles_update_anon_authenticated
// lets org admins/owners and staff update the whole row, including this
// column -- RLS is row-level, not column-level, so it can't distinguish
// "the member consenting for themselves" from "staff setting it on someone
// else's behalf." set_peer_messaging_opt_in() (SECURITY DEFINER,
// supabase/migrations/20260810141500_dm_relationship_scoped_recipients.sql)
// is the only path left that can actually change this column -- direct
// UPDATE on it is column-level REVOKEd for authenticated/anon in that same
// migration -- and it verifies the caller IS the linked person.
export function useSetPeerMessagingOptIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { personId: string; optIn: boolean }) => {
      const { error } = await supabase.rpc("set_peer_messaging_opt_in" as never, {
        p_person_id: params.personId,
        p_opt_in: params.optIn,
      } as never);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directory-share-flags"] });
    },
  });
}

export function useNotificationPreferences(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["notification-preferences", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return null;

      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as NotificationPreference | null;
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useUpsertNotificationPreferences() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: Omit<NotificationPreference, "id" | "created_at" | "updated_at" | "organization_id">) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: existing, error: existingError } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", input.person_id)
        .maybeSingle();

      if (existingError) throw existingError;

      const { data, error } = await supabase
        .from("notification_preferences")
        .upsert({
          organization_id: organizationId,
          person_id: input.person_id,
          email_enabled: input.email_enabled,
          sms_enabled: input.sms_enabled,
          push_enabled: input.push_enabled,
          digest_frequency: input.digest_frequency,
          quiet_hours_start: input.quiet_hours_start,
          quiet_hours_end: input.quiet_hours_end,
          category_preferences: input.category_preferences ?? {},
          updated_at: new Date().toISOString(),
        }, { onConflict: "organization_id,person_id" })
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;

      const action = existing ? "update_notification_preferences" : "create_notification_preferences";
      await logAudit({
        tableName: "notification_preferences",
        recordId: data.id,
        action,
        organizationId,
        userId: user?.id ?? null,
        oldData: (existing as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });
      return data as NotificationPreference;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["notification-preferences", organizationId, data.person_id],
      });
      toast.success("Notification preferences saved");
    },
    onError: (error) => {
      console.error("Failed to save notification preferences:", error);
      toast.error("Failed to save notification preferences");
    },
  });
}

export function useMemberNotifications(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-notifications", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("id, organization_id, person_id, title, body, category, is_read, read_at, link_url, link_label, created_at")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as MemberNotification[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useGroupMaterials(groupId: string | undefined, enabled = true) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["group-materials", organizationId, groupId],
    queryFn: async () => {
      if (!organizationId || !groupId) return [];

      const { data, error } = await supabase
        .from("group_materials")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("group_id", groupId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as GroupMaterial[];
    },
    enabled: Boolean(organizationId && groupId && enabled),
  });
}

export function useAddGroupMaterial() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateGroupMaterialInput) => {
      if (!organizationId) throw new Error("No organization selected");
      if (!input.groupId) throw new Error("Missing group");

      const trimmedTitle = input.title?.trim() || null;
      const trimmedDescription = input.description?.trim() || null;
      const trimmedUrl = input.url?.trim() || null;

      let materialUrl = trimmedUrl;
      let filePath: string | null = null;
      let fileName: string | null = null;
      let mimeType: string | null = null;

      if (input.file) {
        const sanitizedName = input.file.name.replace(/\s+/g, "-");
        filePath = `${organizationId}/groups/${input.groupId}/${Date.now()}-${sanitizedName}`;

        const { error: uploadError } = await supabase.storage
          .from("group-materials")
          .upload(filePath, input.file, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: signedData } = await supabase.storage.from("group-materials").createSignedUrl(filePath, 900);
        materialUrl = signedData?.signedUrl ?? "";
        fileName = input.file.name;
        mimeType = input.file.type || null;
      }

      const { data, error } = await supabase
        .from("group_materials")
        .insert({
          organization_id: organizationId,
          group_id: input.groupId,
          title: trimmedTitle,
          description: trimmedDescription,
          url: materialUrl,
          file_path: filePath,
          file_name: fileName,
          mime_type: mimeType,
          created_by: user?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as GroupMaterial;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["group-materials", organizationId, input.groupId] });
      toast.success("Group material added");
    },
    onError: (error) => {
      console.error("Failed to add group material:", error);
      toast.error("Failed to add group material");
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ notificationId }: { notificationId: string }) => {
      if (!organizationId) throw new Error("No organization selected");
      const { data, error } = await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as MemberNotification;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["member-notifications", organizationId, data.person_id],
      });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ personId }: { personId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .eq("is_read", false);

      if (error) throw error;
      return personId;
    },
    onSuccess: (personId) => {
      queryClient.invalidateQueries({
        queryKey: ["member-notifications", organizationId, personId],
      });
    },
  });
}

const isAnnouncementPublished = (announcement: MemberAnnouncement, referenceDate: Date) => {
  if (announcement.status && announcement.status !== "published") {
    return false;
  }

  if (announcement.published_at) {
    const publishedAt = new Date(announcement.published_at);
    if (publishedAt > referenceDate) return false;
  }

  if (announcement.starts_at) {
    const startsAt = new Date(announcement.starts_at);
    if (startsAt > referenceDate) return false;
  }

  if (announcement.ends_at) {
    const endsAt = new Date(announcement.ends_at);
    if (endsAt < referenceDate) return false;
  }

  return true;
};

const isBulletinPublished = (bulletin: MemberBulletin) => {
  if (bulletin.status) {
    return bulletin.status === "published";
  }

  if (bulletin.published_at) {
    return new Date(bulletin.published_at) <= new Date();
  }

  if (typeof bulletin.is_published === "boolean") {
    return bulletin.is_published;
  }

  return false;
};

export function useMemberAnnouncements(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-announcements", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const now = new Date();
      const { data, error } = await supabase
        .from("announcements")
        .select("id, title, summary, body, created_at, status, published_at, starts_at, ends_at, pinned_until")
        .eq("organization_id", organizationId)
        // Staff mark an announcement Internal to keep it from the congregation.
        .eq("is_internal", false)
        .order("starts_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      const mapped = ((data || []) as MemberAnnouncement[]).map((announcement) => {
        const pinnedUntil = announcement.pinned_until ? new Date(announcement.pinned_until) : null;
        return {
          ...announcement,
          pinned: pinnedUntil ? pinnedUntil >= now : false,
        };
      });

      return mapped.filter((announcement) => isAnnouncementPublished(announcement, now));
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberBulletin(weekOf: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-bulletin", organizationId, weekOf],
    queryFn: async () => {
      if (!organizationId || !weekOf) return null;

      const { data, error } = await supabase
        .from("bulletins")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("week_of", weekOf)
        .order("published_at", { ascending: false });

      if (error) throw error;

      const bulletins = ((data || []) as unknown) as MemberBulletin[];
      return bulletins.find(isBulletinPublished) ?? null;
    },
    enabled: !!organizationId && !!weekOf,
  });
}

export function useMemberBulletinDisabledSectionTypes(weekStartIso: string | undefined, weekEndIso: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-bulletin-disabled-sections", organizationId, weekStartIso, weekEndIso],
    queryFn: async (): Promise<Set<BulletinSectionKey>> => {
      if (!organizationId || !weekStartIso || !weekEndIso) return new Set();
      const { data, error } = await supabase
        .from("bulletins")
        .select("status, published_at, bulletin_sections (type, config_json)")
        .eq("organization_id", organizationId)
        .gte("week_of", weekStartIso)
        .lt("week_of", weekEndIso)
        .order("published_at", { ascending: false });
      if (error) throw error;
      const rows = ((data || []) as unknown) as MemberBulletin[];
      const published = rows.find(isBulletinPublished);
      const sections = (published as { bulletin_sections?: Array<{ type: BulletinSectionKey; config_json?: Record<string, unknown> | null }> } | undefined)?.bulletin_sections ?? [];
      return computeDisabledSectionTypes(sections);
    },
    enabled: !!organizationId && !!weekStartIso && !!weekEndIso,
  });
}

export function useMemberRequests(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-requests", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("member_requests")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return ((data || []) as unknown) as MemberRequest[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberPortalRequests(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-portal-requests", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("requests")
        .select("id, organization_id, requester_person_id, triage_owner_id, summary, details, category, status, visibility, created_at, updated_at")
        .eq("requester_person_id", personId)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        if (isSupabaseUnavailableError(error)) return [];
        throw error;
      }

      return ((data || []) as Record<string, unknown>[])
        .map(normalizePortalRequest) as unknown as MemberPortalRequest[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useCreateMemberRequest() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ personId, subject, description }: { personId: string; subject: string; description?: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from("member_requests")
        .insert({
          organization_id: organizationId,
          person_id: personId,
          subject,
          description: description ?? null,
          status: "open",
          request_type: "member_portal",
        })
        .select()
        .single();

      if (error) throw error;
      return data as unknown as MemberRequest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["member-requests", organizationId, data.person_id],
      });
      toast.success("Request submitted");
    },
    onError: (error) => {
      console.error("Failed to submit request:", error);
      toast.error("Failed to submit request");
    },
  });
}

export function useCreateMemberPortalRequest() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      personId: string;
      title: string;
      description?: string;
      type: string;
      visibility: string;
    }) => {
      if (!organizationId || !user) throw new Error("Not authenticated or no organization selected");

      // Map member-portal type values to v2 category values
      const mapTypeToCategory = (type: string): string => {
        switch (type) {
          case 'pastoral_care': return 'care';
          case 'care': return 'care';
          case 'facility': return 'facility';
          case 'admin': case 'tech': case 'volunteer': return 'admin';
          default: return 'other';
        }
      };

      // Member portal requests have no triage owner yet (assigned by staff later).
      // triage_owner_id references people.id, NOT auth.users.id.
      const insertData = buildCanonicalRequestInsert({
        organizationId,
        triageOwnerId: null,
        summary: input.title,
        category: mapTypeToCategory(input.type),
        details: input.description ?? null,
        requesterPersonId: input.personId,
        visibility: input.visibility || "normal",
        priority: "normal",
        channel: "member_portal",
      });

      const { data, error } = await supabase
        .from("requests")
        .insert(insertData as TablesInsert<"requests">)
        .select()
        .single();

      if (error) throw error;
      return data as MemberPortalRequest;
    },
    onSuccess: (data) => {
      const personId = data.requester_person_id;
      queryClient.invalidateQueries({ queryKey: ["member-portal-requests", organizationId, personId] });
      queryClient.invalidateQueries({ queryKey: ["requests", organizationId] });
      toast.success("Request submitted");

      // Member portal submissions are unassigned by design, so fan out the same
      // needs-triage notifications admins receive for staff-created requests.
      void notifyRequestCreated({
        organizationId: data.organization_id,
        requestId: data.id,
        summary: data.summary,
        category: data.category ?? "other",
        triageOwnerPersonId: data.triage_owner_id ?? null,
        creatorUserId: user?.id ?? null,
      });
    },
    onError: (error) => {
      console.error("Failed to submit request:", error);
      toast.error("Failed to submit request");
    },
  });
}

export function useGivingStatements(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["giving-statements", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("giving_statements")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("statement_year", { ascending: false });

      if (error) throw error;
      return ((data || []) as unknown) as GivingStatement[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberGivingSummary(personId: string | undefined) {
  const { organizationId } = useOrganization();
  const { settings: orgSettings } = useOrgSettings();
  const orgNow = useOrgMidnightTick(orgSettings.timezone);
  const organizationToday = timeZoneDateYmd(orgSettings.timezone, orgNow);

  return useQuery({
    queryKey: [
      "member-giving-summary",
      organizationId,
      personId,
      orgSettings.timezone,
      organizationToday,
    ],
    queryFn: async () => {
      if (!organizationId || !personId) {
        return { ytdTotal: 0, lastYearTotal: 0, lastGiftDate: null } as MemberGivingSummary;
      }

      const currentYear = Number(organizationToday.slice(0, 4));
      const currentYearStart = `${currentYear}-01-01`;
      const lastYearStart = `${currentYear - 1}-01-01`;
      const lastYearEnd = `${currentYear - 1}-12-31`;

      const contributionRows = await fetchAllRows<{
        id: string;
        amount: number;
        date: string;
        status: string | null;
        batch_source: string | null;
        deleted_at: string | null;
        void_reason: string | null;
      }>((from, to) => supabase
        .from("contributions")
        .select("id, amount, date, status, batch_source, deleted_at, void_reason")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .is("deleted_at", null)
        .gte("date", lastYearStart)
        .lte("date", organizationToday)
        .order("date", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to));

      const contributions = contributionRows.filter(isCountableGivingContributionRecord);
      const ytdTotal = contributions
        .filter((contribution) => contribution.date >= currentYearStart)
        .reduce((sum, contribution) => sum + (contribution.amount ?? 0), 0);

      const lastYearTotal = contributions
        .filter((contribution) => contribution.date >= lastYearStart && contribution.date <= lastYearEnd)
        .reduce((sum, contribution) => sum + (contribution.amount ?? 0), 0);

      const lastGiftDate = contributions
        .map((contribution) => contribution.date)
        .sort((a, b) => b.localeCompare(a))[0] ?? null;

      return { ytdTotal, lastYearTotal, lastGiftDate } as MemberGivingSummary;
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberGivingTransactions({
  personId,
  startDate,
  endDate,
  fundId,
}: {
  personId: string | undefined;
  startDate?: string;
  endDate?: string;
  fundId?: string;
}) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-giving-transactions", organizationId, personId, startDate, endDate, fundId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const contributionsPromise = fetchAllRows<Omit<MemberContribution, "allocations">>((from, to) => {
        let query = supabase
          .from("contributions")
          .select(`
            id,
            amount,
            date,
            method,
            status,
            batch_source,
            deleted_at,
            void_reason
          `)
          .eq("organization_id", organizationId)
          .eq("person_id", personId)
          .is("deleted_at", null);

        if (startDate) query = query.gte("date", startDate);
        if (endDate) query = query.lte("date", endDate);

        return query
          .order("date", { ascending: false })
          .order("id", { ascending: false })
          .range(from, to);
      });

      const [contributionRows, catalog, allocations] = await Promise.all([
        contributionsPromise,
        getMemberGivingFundCatalog(organizationId),
        getMemberGivingAllocations(organizationId, personId),
      ]);
      const namedContributions = addMemberGivingAllocations(contributionRows, catalog, allocations);
      return (fundId
        ? namedContributions.filter((contribution) =>
          contribution.allocations.some((allocation) => allocation.fund?.id === fundId)
        )
        : namedContributions) as MemberContribution[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberRecurringGifts(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-recurring-gifts", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const [{ data, error }, catalog] = await Promise.all([
        supabase
        .from("recurring_gifts")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("next_charge_date", { ascending: true }),
        getMemberGivingFundCatalog(organizationId),
      ]);

      if (error) throw error;
      return mapMemberGivingFundNames(
        (data || []) as MemberRecurringGift[],
        catalog,
      ) as MemberRecurringGift[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberGivingFunds() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-giving-funds", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const catalog = await getMemberGivingFundCatalog(organizationId);
      return catalog.filter((fund) => fund.is_active && fund.online_giving_enabled);
    },
    enabled: !!organizationId,
  });
}

export function useCreateRecurringGift() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      personId,
      amount,
      fundId,
      frequency,
      startDate,
    }: {
      personId: string;
      amount: number;
      fundId: string;
      frequency: RecurringFrequency;
      startDate: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: gift, error: giftError } = await supabase
        .from("recurring_gifts")
        .insert({
          organization_id: organizationId,
          person_id: personId,
          fund_id: fundId,
          amount,
          frequency,
          start_date: startDate,
          next_charge_date: startDate,
          status: "active",
        })
        .select()
        .single();

      if (giftError || !gift) {
        throw giftError ?? new Error("Unable to create recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: gift.id,
        action: "create_recurring_gift",
        organizationId,
        oldData: null,
        newData: gift as Record<string, unknown> | null,
      });

      return gift as MemberRecurringGift;
    },
    onSuccess: (gift) => {
      queryClient.invalidateQueries({ queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id] });
      queryClient.invalidateQueries({ queryKey: ["member-giving-transactions", gift.organization_id, gift.person_id] });
      toast.success("Recurring gift created");
    },
    onError: (error) => {
      console.error("Failed to create recurring gift:", error);
      toast.error("Failed to create recurring gift");
    },
  });
}

export function useUpdateRecurringGift() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({
      giftId,
      amount,
      fundId,
      frequency,
      nextChargeDate,
    }: {
      giftId: string;
      amount: number;
      fundId: string;
      frequency: RecurringFrequency;
      nextChargeDate: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("recurring_gifts")
        .select("*")
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("recurring_gifts")
        .update({
          amount,
          fund_id: fundId,
          frequency,
          next_charge_date: nextChargeDate,
          updated_at: new Date().toISOString(),
        })
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error || !data) {
        throw error ?? new Error("Unable to update recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: giftId,
        action: "update_recurring_gift",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data as MemberRecurringGift;
    },
    onSuccess: (gift) => {
      queryClient.invalidateQueries({ queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id] });
      toast.success("Recurring gift updated");
    },
    onError: (error) => {
      console.error("Failed to update recurring gift:", error);
      toast.error("Failed to update recurring gift");
    },
  });
}

export function usePauseRecurringGift() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ giftId }: { giftId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("recurring_gifts")
        .select("*")
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("recurring_gifts")
        .update({
          status: "paused",
          paused_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error || !data) {
        throw error ?? new Error("Unable to pause recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: giftId,
        action: "pause_recurring_gift",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data as MemberRecurringGift;
    },
    onSuccess: (gift) => {
      queryClient.invalidateQueries({ queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id] });
      toast.success("Recurring gift paused");
    },
    onError: (error) => {
      console.error("Failed to pause recurring gift:", error);
      toast.error("Failed to pause recurring gift");
    },
  });
}

/**
 * Skip (or un-skip) the NEXT occurrence of a recurring gift without pausing it.
 * The process-recurring-gifts cron honours skip_next_charge: on the next due
 * run it advances the schedule one period and clears the flag, charging nothing.
 */
export function useSkipNextRecurringGift() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ giftId, skip }: { giftId: string; skip: boolean }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("recurring_gifts")
        .select("*")
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("recurring_gifts")
        .update({
          skip_next_charge: skip,
          updated_at: new Date().toISOString(),
        })
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error || !data) {
        throw error ?? new Error("Unable to update recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: giftId,
        action: skip ? "skip_next_recurring_gift" : "unskip_next_recurring_gift",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data as MemberRecurringGift;
    },
    onSuccess: (gift, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id],
      });
      toast.success(variables.skip ? "Next gift skipped" : "Skip undone");
    },
    onError: (error) => {
      console.error("Failed to update recurring gift skip:", error);
      toast.error("Couldn't update the recurring gift");
    },
  });
}

export function useResumeRecurringGift() {
  const queryClient = useQueryClient();
  const { organizationId, organization } = useOrganization();

  return useMutation({
    mutationFn: async ({ gift }: { gift: MemberRecurringGift }) => {
      if (!organizationId) throw new Error("No organization selected");
      const organizationTimeZone =
        typeof organization?.settings?.timezone === "string"
          ? organization.settings.timezone
          : "America/New_York";
      const organizationToday = parseLocalDate(timeZoneDateYmd(organizationTimeZone)) ?? new Date();
      const nextChargeDate = ensureFutureChargeDate(
        gift.next_charge_date,
        gift.frequency,
        organizationToday,
      );
      const { data, error } = await supabase
        .from("recurring_gifts")
        .update({
          status: "active",
          paused_at: null,
          next_charge_date: nextChargeDate,
          updated_at: new Date().toISOString(),
        })
        .eq("id", gift.id)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error || !data) {
        throw error ?? new Error("Unable to resume recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: gift.id,
        action: "resume_recurring_gift",
        organizationId,
        oldData: gift as unknown as Record<string, unknown>,
        newData: data as Record<string, unknown> | null,
      });

      return data as MemberRecurringGift;
    },
    onSuccess: (gift) => {
      queryClient.invalidateQueries({ queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id] });
      toast.success("Recurring gift resumed");
    },
    onError: (error) => {
      console.error("Failed to resume recurring gift:", error);
      toast.error("Failed to resume recurring gift");
    },
  });
}

export function useCancelRecurringGift() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ giftId }: { giftId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("recurring_gifts")
        .select("*")
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("recurring_gifts")
        .update({
          status: "canceled",
          canceled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", giftId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error || !data) {
        throw error ?? new Error("Unable to cancel recurring gift");
      }

      await logAudit({
        tableName: "recurring_gifts",
        recordId: giftId,
        action: "cancel_recurring_gift",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data as MemberRecurringGift;
    },
    onSuccess: (gift) => {
      queryClient.invalidateQueries({ queryKey: ["member-recurring-gifts", gift.organization_id, gift.person_id] });
      toast.success("Recurring gift canceled");
    },
    onError: (error) => {
      console.error("Failed to cancel recurring gift:", error);
      toast.error("Failed to cancel recurring gift");
    },
  });
}

export function useGenerateGivingStatement() {
  const { organizationId } = useOrganization();
  const { settings: orgSettings } = useOrgSettings();
  const orgNow = useOrgMidnightTick(orgSettings.timezone);
  const organizationToday = timeZoneDateYmd(orgSettings.timezone, orgNow);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      personId,
      personName,
      startDate,
      endDate,
    }: {
      personId: string;
      personName: string;
      startDate: string;
      endDate: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");
      const effectiveEndDate = clampStatementEndDate(endDate, organizationToday);
      if (startDate > effectiveEndDate) {
        throw new Error("Statement start date must be on or before organization today");
      }

      const [contributions, catalog, allocations] = await Promise.all([
        fetchAllRows((from, to) => supabase
          .from("contributions")
          .select(`
            id,
            amount,
            date,
            created_at,
            method,
            status,
            batch_source,
            deleted_at,
            void_reason
          `)
          .eq("organization_id", organizationId)
          .eq("person_id", personId)
          .is("deleted_at", null)
          .gte("date", startDate)
          .lte("date", effectiveEndDate)
          .order("date", { ascending: true })
          .order("created_at", { ascending: true })
          .order("id", { ascending: true })
          .range(from, to)),
        getMemberGivingFundCatalog(organizationId),
        getMemberGivingAllocations(organizationId, personId),
      ]);

      const namedContributions = addMemberGivingAllocations(
        contributions as MemberContribution[],
        catalog,
        allocations,
      );
      const countableContributions = namedContributions.filter(
        isCountableGivingContributionRecord,
      );
      const totalAmount = countableContributions.reduce((sum, item) => sum + (item.amount ?? 0), 0);

      // Render the member statement with the same IRS Pub. 1771 rich PDF staff use
      // (logo/accent/header/footer + EIN), instead of the bare text-only generator.
      // organizations.ein is the canonical EIN (set via the Giving Setup Wizard).
      // The react-pdf bundle is lazy-loaded, so it only ships when a member taps
      // "Generate statement".
      const { data: org } = await supabase
        .from("organizations")
        .select(
          "name, logo_url, accent_color, statement_header_text, statement_footer_text, legal_name, ein"
        )
        .eq("id", organizationId)
        .single();

      const { buildStaffGivingStatementPdf } = await loadStaffGivingStatementPdf();

      const pdfBytes = await buildStaffGivingStatementPdf({
        organizationName: org?.name || "Church",
        organizationLogo: org?.logo_url,
        accentColor: org?.accent_color,
        legalName: org?.legal_name,
        ein: org?.ein,
        householdName: personName,
        statementPeriod: `${safeFormatDate(startDate, "MMM d, yyyy", startDate)} – ${safeFormatDate(effectiveEndDate, "MMM d, yyyy", effectiveEndDate)}`,
        totalAmount,
        contributions: countableContributions.map((item) => {
          const itemAllocations = item.allocations ?? [];
          // Mirrors the staff-side computation in Contributions.tsx: a
          // contribution is only fully deductible if every allocation's
          // fund is. Sum per-allocation, not per-contribution: an allocation
          // to a non-deductible fund is entirely non-deductible regardless of
          // its own non_deductible_amount (which only tracks the FMV/quid-pro-
          // quo portion of an allocation to a DEDUCTIBLE fund) — otherwise a
          // mixed gift (part deductible fund, part non-deductible fund) would
          // be silently treated as 100% deductible.
          const allAllocationsNonDeductible =
            itemAllocations.length > 0 &&
            itemAllocations.every((allocation) => allocation.fund?.is_tax_deductible === false);
          // Track the fund-designation portion separately from the FMV
          // portion: they are legally distinct (FMV = goods/services
          // received in exchange; fund-designation = the org has simply
          // classified that fund as non-deductible). Conflating them would
          // make the printed disclosure falsely claim goods/services were
          // provided when the real reason is fund classification.
          //
          // The two are NOT mutually exclusive on a single allocation — an
          // allocation to a non-deductible fund (e.g. event registration)
          // can still separately record an FMV/quid-pro-quo amount. Track
          // fmvAmount independent of fund deductibility so that FMV is never
          // discarded; nonDeductibleFundAmount is then whatever's left after
          // FMV is accounted for (avoids double-counting the same dollars
          // under both reasons).
          let fmvAmount = 0;
          const nonDeductibleAmount = itemAllocations.reduce((sum, allocation) => {
            const allocAmount = Number(allocation.amount ?? 0);
            const allocFmv = Math.min(Number(allocation.non_deductible_amount ?? 0), allocAmount);
            fmvAmount += allocFmv;
            if (allocation.fund?.is_tax_deductible === false) {
              return sum + allocAmount;
            }
            return sum + allocFmv;
          }, 0);
          const nonDeductibleFundAmount = nonDeductibleAmount - fmvAmount;

          return {
            date: safeFormatDate(item.date, "MMM d, yyyy", item.date),
            amount: item.amount,
            method: item.method,
            status: item.status,
            batchSource: item.batch_source,
            fund: summarizeContributionAllocations(item.allocations, {
              emptyLabel: "General",
              fallbackFundName: "General",
            }),
            isTaxDeductible: !allAllocationsNonDeductible,
            nonDeductibleFundAmount,
            nonDeductibleAmount,
          };
        }),
        headerText: org?.statement_header_text,
        footerText: org?.statement_footer_text || undefined,
      });

      const filePath = `${organizationId}/statements/${personId}/${Date.now()}-${startDate}-to-${effectiveEndDate}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from(STATEMENT_ARTIFACT_BUCKET)
        .upload(filePath, new Blob([pdfBytes as BlobPart], { type: "application/pdf" }), { upsert: false });

      if (uploadError) throw uploadError;

      const { data: statement, error: statementError } = await supabase
        .from("giving_statements")
        .insert({
          organization_id: organizationId,
          person_id: personId,
          statement_year: getStatementYear(startDate),
          total_amount: totalAmount,
          statement_url: null,
          artifact_bucket: STATEMENT_ARTIFACT_BUCKET,
          artifact_path: filePath,
          status: "available",
        })
        .select()
        .single();

      if (statementError) throw statementError;

      return statement as unknown as GivingStatement;
    },
    onSuccess: (statement) => {
      queryClient.invalidateQueries({ queryKey: ["giving-statements", organizationId, statement.person_id] });
      toast.success("Statement generated");
    },
    onError: (error) => {
      console.error("Failed to generate statement:", error);
      const message =
        error instanceof Error && error.message?.includes("storage")
          ? "Statement generation is temporarily unavailable. Please contact your church admin."
          : "Failed to generate statement. Please try again.";
      toast.error(message);
    },
  });
}

export function useMemberDirectoryEntries() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-directory-entries", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc(
        "get_member_directory_entries",
        { p_organization_id: organizationId }
      );

      if (error) throw error;
      return (data ?? []) as MemberDirectoryEntry[];
    },
    enabled: !!organizationId,
  });
}

export function useMemberGroups(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-groups", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("group_members")
        .select(`
          id,
          group_id,
          person_id,
          organization_id,
          role,
          group:groups(
            id,
            organization_id,
            name,
            description,
            is_open,
            is_discoverable
          )
        `)
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return ((data || []) as unknown) as MemberGroupMembership[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useDiscoverableGroups(enabled: boolean) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["discoverable-groups", organizationId],
    queryFn: async () => {
      if (!organizationId || !enabled) return [];

      const { data, error } = await supabase
        .from("groups")
        .select("id, organization_id, name, description, is_open, is_discoverable")
        .eq("organization_id", organizationId)
        .eq("is_open", true)
        .eq("is_discoverable", true)
        .order("name", { ascending: true });

      if (error) throw error;
      return ((data || []) as unknown) as MemberGroup[];
    },
    enabled: !!organizationId && enabled,
  });
}

/** Enrichment data for group cards: member count, avatar previews, and next meeting. */
export interface GroupEnrichment {
  groupId: string;
  memberCount: number;
  leaderCount: number;
  avatarPreviews: { id: string; first_name: string; last_name: string; photo_url: string | null }[];
  nextMeeting: { meeting_date: string; start_time: string | null; title: string | null } | null;
}

export function useGroupEnrichments(groupIds: string[]) {
  const { organizationId } = useOrganization();
  const stableIds = JSON.stringify(groupIds.slice().sort());

  return useQuery({
    queryKey: ["group-enrichments", organizationId, stableIds],
    queryFn: async () => {
      if (!organizationId || groupIds.length === 0) return new Map<string, GroupEnrichment>();

      // Fetch all member rows for these groups
      const { data: members, error: membersError } = await supabase
        .from("group_members")
        .select(`
          group_id,
          role,
          person:people(id, first_name, last_name, photo_url)
        `)
        .eq("organization_id", organizationId)
        .in("group_id", groupIds);
      if (membersError) throw membersError;

      // Fetch upcoming meetings
      const today = localDateYmd();
      const { data: meetings, error: meetingsError } = await supabase
        .from("group_meetings")
        .select("group_id, meeting_date, start_time, title")
        .eq("organization_id", organizationId)
        .in("group_id", groupIds)
        .gte("meeting_date", today)
        .order("meeting_date", { ascending: true });
      if (meetingsError) throw meetingsError;

      const result = new Map<string, GroupEnrichment>();

      for (const gid of groupIds) {
        const groupMembers = (members || []).filter((m) => m.group_id === gid);
        const groupMeetings = (meetings || []).filter((m) => m.group_id === gid);

        const avatarPreviews = groupMembers
          .slice(0, 5)
          .map((m) => {
            const p = m.person as { id: string; first_name: string; last_name: string; photo_url: string | null } | null;
            return p ? { id: p.id, first_name: p.first_name, last_name: p.last_name, photo_url: p.photo_url } : null;
          })
          .filter(Boolean) as GroupEnrichment["avatarPreviews"];

        result.set(gid, {
          groupId: gid,
          memberCount: groupMembers.length,
          leaderCount: groupMembers.filter((m) => m.role === "leader").length,
          avatarPreviews,
          nextMeeting: groupMeetings[0]
            ? { meeting_date: groupMeetings[0].meeting_date, start_time: groupMeetings[0].start_time, title: groupMeetings[0].title }
            : null,
        });
      }

      return result;
    },
    enabled: !!organizationId && groupIds.length > 0,
  });
}

export function useGroupDetail(groupId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["group-detail", organizationId, groupId],
    queryFn: async () => {
      if (!organizationId || !groupId) return null;

      const { data, error } = await supabase
        .from("groups")
        .select("id, organization_id, name, description, is_open, is_discoverable")
        .eq("organization_id", organizationId)
        .eq("id", groupId)
        .maybeSingle();

      if (error) throw error;
      return ((data || null) as unknown) as MemberGroup | null;
    },
    enabled: !!organizationId && !!groupId,
  });
}

export function useGroupMembership(personId: string | undefined, groupId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-group-membership", organizationId, personId, groupId],
    queryFn: async () => {
      if (!organizationId || !personId || !groupId) return null;

      const { data, error } = await supabase
        .from("group_members")
        .select("id, group_id, person_id, organization_id, role")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .eq("group_id", groupId)
        .maybeSingle();

      if (error) throw error;
      return ((data || null) as unknown) as Omit<MemberGroupMembership, "group"> | null;
    },
    enabled: !!organizationId && !!personId && !!groupId,
  });
}

export function useGroupRoster(groupId: string | undefined, enabled = true) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["group-roster", organizationId, groupId],
    queryFn: async (): Promise<GroupRosterResult> => {
      // countExact false: nothing was asked of the server, so completeness is
      // unknown rather than confirmed. `enabled` normally keeps us out of here.
      if (!organizationId || !groupId) {
        return {
          entries: [],
          hiddenCount: 0,
          hiddenByPermission: 0,
          hiddenByTruncation: 0,
          hiddenLeadersByPermission: 0,
          hiddenLeaderCount: 0,
          countExact: false,
        };
      }

      // count: "exact" is load-bearing, not diagnostics. PostgREST caps
      // responses at max_rows = 1000 (supabase/config.toml), and this query has
      // no pagination, so a group larger than that comes back silently
      // truncated with every returned embed readable. Without the total, a
      // 1200-member group would report hiddenCount 0 and read as COMPLETE
      // (Codex P1 on #6092). The count is of group_members rows matching the
      // filters, independent of the row cap.
      const {
        data: members,
        error,
        count: totalMemberRows,
      } = await supabase
        .from("group_members")
        .select(
          `
          id,
          role,
          person:people(
            id,
            first_name,
            last_name,
            email,
            phone,
            photo_url,
            household_id
          )
        `,
          { count: "exact" },
        )
        .eq("organization_id", organizationId)
        .eq("group_id", groupId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // PostgREST returns `person: null` for a row whose embedded people record
      // the caller cannot read, and `group_members` is readable far more widely
      // than `people` is: `group_members_select_authenticated` admits any group
      // participant, while `people_select_authenticated` only exposes another
      // person's row to specific named staff roles. A group leader holding no
      // org role therefore gets rows with null persons.
      //
      // Drop them here rather than at each use site. `GroupRosterEntry.person`
      // is declared non-nullable and ~35 call sites in MemberGroupDetail.tsx
      // dereference it directly (`entry.person.id`, `a.person.last_name`), so a
      // null would throw during render. Filtering once keeps the declared type
      // honest for every consumer (#5974).
      //
      // NOTE: this makes the roster "entries you are permitted to see", so a
      // non-staff leader sees a short roster instead of a crash. Showing them
      // the full roster needs a people RLS branch for group co-membership, or a
      // user-facing column on get_group_roster_for_leader -- a migration, and
      // so a human decision. That half of #5974 is deliberately not fixed here.
      const rawRows = (members || []) as Omit<GroupRosterEntry, "privacy">[];
      const roster = rawRows.filter(
        (entry) => entry.person !== null && entry.person !== undefined,
      );
      // Everyone in the group this caller cannot act on, from BOTH causes:
      // rows the server never sent (max_rows truncation) and rows whose person
      // embed RLS nulled. Deriving it from the server's own total rather than
      // from the returned page is what makes truncation visible.
      //
      // `count` is nullable. When it is missing we fall back to the page length
      // so the RLS-null half stays accurate, but the fallback CANNOT distinguish
      // a complete page from a truncated one -- so `countExact` goes out with it
      // and callers must treat the whole result as unknown rather than complete
      // (Codex P2 round 7 on #6092).
      const countExact = totalMemberRows !== null && totalMemberRows !== undefined;
      const knownTotal = countExact ? (totalMemberRows as number) : rawRows.length;
      // Kept split by cause, not just totalled: RLS withholding a row is a
      // permission problem an administrator can act on, while max_rows
      // truncation blocks that administrator identically. Collapsing them sends
      // large-group users to someone who is just as stuck (Codex P2 round 8).
      const hiddenByTruncation = Math.max(0, knownTotal - rawRows.length);
      const hiddenByPermission = Math.max(0, rawRows.length - roster.length);
      const hiddenCount = hiddenByPermission + hiddenByTruncation;
      // Rows the server sent but RLS nulled: `role` comes from group_members,
      // which is readable, so we know exactly which of these are leaders.
      const hiddenLeadersByPermission = rawRows.filter(
        (entry) =>
          (entry.person === null || entry.person === undefined) &&
          entry.role === "leader",
      ).length;
      // Rows the server never sent carry no role, so they count as possible
      // leaders -- a leaders-only send must not be enabled on a maybe.
      const hiddenLeaderCount = hiddenLeadersByPermission + hiddenByTruncation;
      const personIds = roster.map((entry) => entry.person?.id).filter(Boolean) as string[];

      if (personIds.length === 0) {
        return {
          entries: roster.map((entry) => ({ ...entry, privacy: null })),
          hiddenCount,
          hiddenByPermission,
          hiddenByTruncation,
          hiddenLeadersByPermission,
          hiddenLeaderCount,
          countExact,
        };
      }

      const { data: privacyRows, error: privacyError } = await supabase
        .from("directory_profiles")
        .select("person_id, share_phone, share_address, share_birthday")
        .eq("organization_id", organizationId)
        .in("person_id", personIds);

      if (privacyError) throw privacyError;

      const privacyMap = new Map(
        (privacyRows || []).map((row: any) => [row.person_id, row]),
      );

      return {
        entries: roster.map((entry) => ({
          ...entry,
          privacy: privacyMap.get(entry.person?.id ?? "") ?? null,
        })) as GroupRosterEntry[],
        hiddenCount,
        hiddenByPermission,
        hiddenByTruncation,
        hiddenLeadersByPermission,
        hiddenLeaderCount,
        countExact,
      };
    },
    enabled: !!organizationId && !!groupId && enabled,
  });
}

export function useGroupMeetings(groupId: string | undefined, enabled = true) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["group-meetings", organizationId, groupId],
    queryFn: async () => {
      if (!organizationId || !groupId) return [];

      const today = localDateYmd();
      const { data, error } = await supabase
        .from("group_meetings")
        .select("id, organization_id, group_id, title, meeting_date, start_time, end_time, location, description")
        .eq("organization_id", organizationId)
        .eq("group_id", groupId)
        .gte("meeting_date", today)
        .order("meeting_date", { ascending: true });

      if (error) throw error;
      return ((data || []) as unknown) as GroupMeeting[];
    },
    enabled: !!organizationId && !!groupId && enabled,
  });
}

export function useGroupAttendance(
  groupId: string | undefined,
  meetingDate: string,
  eventName: string | undefined,
  enabled = true
) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["group-attendance", organizationId, groupId, meetingDate, eventName],
    queryFn: async () => {
      if (!organizationId || !groupId || !meetingDate || !eventName) return [];

      const { data, error } = await supabase
        .from("attendance")
        .select("id, person_id, event_date")
        .eq("organization_id", organizationId)
        .eq("event_date", meetingDate)
        .eq("event_name", eventName);

      if (error) throw error;
      return (data || []).map((record: any) => ({
        id: record.id,
        organization_id: organizationId,
        group_id: groupId,
        person_id: record.person_id,
        meeting_date: record.event_date,
        present: true,
      })) as GroupAttendance[];
    },
    enabled: !!organizationId && !!groupId && !!eventName && enabled,
  });
}

export function useMemberGroupJoinRequests(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-group-join-requests", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("group_join_requests")
        .select("id, organization_id, person_id, group_id, status, message, requested_at, resolved_at, group:groups(name)")
        .eq("organization_id", organizationId)
        .eq("person_id", personId);

      if (error) throw error;
      return (data || []).map((row) => ({
        id: row.id,
        organization_id: row.organization_id,
        person_id: row.person_id,
        group_id: row.group_id,
        title: row.group?.name ? `Request to join ${row.group.name}` : undefined,
        description: row.message,
        status: row.status,
        created_at: row.requested_at,
        updated_at: row.resolved_at ?? row.requested_at,
      })) as MemberRequest[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useGroupJoinRequests(groupId: string | undefined) {
  return useQuery({
    queryKey: ["group-join-requests", groupId],
    queryFn: async () => {
      if (!groupId) return [];

      // Goes through get_pending_join_requests_for_leader rather than a
      // direct select with an embedded people() relation: people_select_
      // authenticated only exposes another person's row to specific named
      // staff roles, which a plain group_members.role = 'leader' doesn't
      // satisfy, so the embed would always resolve to null for a non-staff
      // leader. Mirrors GroupFinder.tsx's identical read for the same
      // reason. That RPC only ever returns 'pending' rows and name/message,
      // not email/phone/photo_url -- those genuinely aren't available to a
      // non-staff leader through this path.
      const { data, error } = await supabase.rpc("get_pending_join_requests_for_leader", {
        p_group_ids: [groupId],
      });

      if (error) throw error;
      return (data || []).map((row) => {
        // The RPC returns NULL for a requester whose name parts are both blank.
        const [first_name, ...rest] = (row.person_name ?? "Unknown").split(" ");
        return {
          id: row.id,
          organization_id: "",
          person_id: row.person_id,
          group_id: row.group_id,
          description: row.message,
          status: "pending",
          created_at: row.requested_at,
          updated_at: row.requested_at,
          person: {
            id: row.person_id,
            first_name,
            last_name: rest.join(" ") || null,
            email: null,
            phone: null,
            photo_url: null,
          },
        };
      }) as GroupJoinRequest[];
    },
    enabled: !!groupId,
  });
}

export function useRequestGroupJoin() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      group: MemberGroup;
      personId: string;
      personName: string;
      note?: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: request, error } = await supabase
        .from("group_join_requests")
        .insert({
          organization_id: organizationId,
          person_id: input.personId,
          group_id: input.group.id,
          status: "pending",
          message: input.note ?? null,
        })
        .select()
        .single();

      if (error) throw error;

      // Leaders are notified by the notify_group_leaders_of_join_request
      // trigger (#5554). A client insert into notifications was rejected by
      // RLS for portal users and only ever reached console.error.
      return request as unknown as MemberRequest;
    },
    onSuccess: (_request, variables) => {
      queryClient.invalidateQueries({ queryKey: ["member-group-join-requests", organizationId, variables.personId] });
      toast.success("Group join request sent");
    },
    onError: (error) => {
      console.error("Failed to request group join:", error);
      toast.error("Failed to request group join");
    },
  });
}

/**
 * Removes an answered request from the leader's list at once, rather than waiting on the refetch:
 * a refetch that fails or is cancelled (invalidating again cancels one in flight) would otherwise
 * leave the resolved row on screen, and the RPC rejects a second answer to it (Codex P2 on #7137;
 * GroupFinder does the same after PR #6333). Runs in onSuccess, which a caller's mutateAsync waits
 * for, so the row is gone before the caller re-enables anything.
 */
function dropAnsweredJoinRequest(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: string | null | undefined,
  requestId: string,
) {
  queryClient.setQueryData<{ id: string }[]>(
    ["group-join-requests", groupId],
    (current) => current?.filter((request) => request.id !== requestId),
  );
}

export function useApproveGroupJoinRequest() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { requestId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      // Fetched before the RPC only to return a stable mutation payload and
      // invalidate the right queries below. The atomic membership insert,
      // status write, and requester notification all happen inside the RPC,
      // which delegates to the existing tenant/capacity/authorization guards.
      const { data: request, error: requestError } = await supabase
        .from("group_join_requests")
        .select("id, person_id, group_id, organization_id")
        .eq("organization_id", organizationId)
        .eq("id", input.requestId)
        .single();

      if (requestError || !request) {
        throw requestError ?? new Error("Unable to approve group join request");
      }

      const { error: rpcError } = await supabase.rpc("approve_group_join_request", {
        p_request_id: input.requestId,
      });

      if (rpcError) throw rpcError;

      return request as unknown as MemberRequest;
    },
    onSuccess: (data) => {
      dropAnsweredJoinRequest(queryClient, data.group_id, data.id);
      queryClient.invalidateQueries({ queryKey: ["member-group-join-requests", organizationId, data.person_id] });
      queryClient.invalidateQueries({ queryKey: ["member-groups", organizationId, data.person_id] });
      queryClient.invalidateQueries({ queryKey: ["group-join-requests", data.group_id] });
      queryClient.invalidateQueries({ queryKey: ["group-roster", organizationId, data.group_id] });
      toast.success("Group join request approved");
    },
    onError: (error) => {
      console.error("Failed to approve group join request:", error);
      toast.error("Failed to approve group join request");
    },
  });
}

export function useDeclineGroupJoinRequest() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { requestId: string; groupId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      // The decline wrapper preserves the response RPC's authorization and
      // already-resolved guards; its existing trigger writes the notice.
      const { error } = await supabase.rpc("decline_group_join_request", {
        p_request_id: input.requestId,
      });

      if (error) throw error;
      return input;
    },
    onSuccess: (input) => {
      dropAnsweredJoinRequest(queryClient, input.groupId, input.requestId);
      queryClient.invalidateQueries({ queryKey: ["group-join-requests", input.groupId] });
      toast.success("Group join request declined");
    },
    onError: (error) => {
      console.error("Failed to decline group join request:", error);
      toast.error("Failed to decline group join request");
    },
  });
}

export function useRemoveGroupMember() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { groupId: string; personId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { error } = await supabase
        .from("group_members")
        .delete()
        .eq("organization_id", organizationId)
        .eq("group_id", input.groupId)
        .eq("person_id", input.personId);

      if (error) throw error;
      return input;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["group-roster", organizationId, data.groupId] });
      queryClient.invalidateQueries({ queryKey: ["group-detail", organizationId, data.groupId] });
      toast.success("Member removed from group");
    },
    onError: (error) => {
      console.error("Failed to remove member:", error);
      toast.error("Failed to remove member");
    },
  });
}

export function useSendGroupMessage() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      senderId: string;
      groupId: string;
      channel: "email" | "sms";
      subject?: string;
      body: string;
      recipients: { personId: string; address: string }[];
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      if (input.recipients.length === 0) {
        throw new Error("No eligible recipients");
      }

      const payload: MessageInsert[] = input.recipients.map((recipient) => ({
        organization_id: organizationId,
        sender_id: input.senderId,
        recipient_person_id: recipient.personId,
        recipient_request_id: null,
        channel: input.channel,
        direction: "outbound",
        recipient_address: recipient.address,
        subject: input.channel === "email" ? input.subject ?? null : null,
        body: input.body,
        status: "pending",
        sent_at: null,
        scheduled_at: null,
        automation_trigger: null,
        template_version: null,
        suppression_status: null,
        metadata: { group_id: input.groupId },
      }));

      return createMessageRecords(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages", organizationId] });
      toast.success("Group message queued");
    },
    onError: (error) => {
      console.error("Failed to send group message:", error);
      toast.error("Failed to send group message");
    },
  });
}

const parseLocalMeetingDate = (meetingDate: string) => {
  const [year, month, day] = meetingDate.split("-").map(Number);
  if (
    Number.isFinite(year) &&
    Number.isFinite(month) &&
    Number.isFinite(day)
  ) {
    return new Date(year, month - 1, day);
  }
  return new Date(meetingDate);
};

const formatGroupMeetingDate = (meeting: GroupMeeting) => {
  const meetingDate = parseLocalMeetingDate(meeting.meeting_date);
  return meetingDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const formatGroupMeetingTime = (meeting: GroupMeeting) => {
  if (!meeting.start_time) return "";
  const start = new Date(`${meeting.meeting_date}T${meeting.start_time}`);
  return start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const resolveReminderScheduleTime = (meeting: GroupMeeting) => {
  const meetingStart = meeting.start_time
    ? new Date(`${meeting.meeting_date}T${meeting.start_time}`)
    : new Date(`${meeting.meeting_date}T09:00:00`);
  const scheduledAt = new Date(meetingStart.getTime() - 24 * 60 * 60 * 1000);
  const now = new Date();
  if (scheduledAt <= now) {
    return new Date(now.getTime() + 5 * 60 * 1000);
  }
  return scheduledAt;
};

const getGroupReminderPreferences = async (
  organizationId: string,
  personIds: string[],
): Promise<GroupReminderPreferences[]> => {
  if (personIds.length === 0) return [];
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("person_id, email_enabled, sms_enabled, category_preferences")
    .eq("organization_id", organizationId)
    .in("person_id", personIds);

  if (error) throw error;
  return (data ?? []) as GroupReminderPreferences[];
};

const isGroupReminderEnabled = (
  preferences: GroupReminderPreferences | undefined,
  channel: "email" | "sms"
) => {
  if (!preferences) return true;
  const categoryPreferences = preferences.category_preferences ?? {};
  const groupsEnabled = typeof categoryPreferences.groups === "boolean"
    ? categoryPreferences.groups
    : true;
  if (!groupsEnabled) return false;
  return channel === "email" ? preferences.email_enabled : preferences.sms_enabled;
};

export function useScheduleGroupMeetingReminders() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      senderId: string;
      group: MemberGroup;
      meeting: GroupMeeting;
      roster: GroupRosterEntry[];
      channel: "email" | "sms";
    }) => {
      type ScheduledMessage = {
        organization_id: string;
        sender_id: string;
        recipient_person_id: string | null;
        recipient_request_id: string | null;
        channel: "email" | "sms";
        direction: "outbound";
        recipient_address: string;
        subject: string | null;
        body: string;
        status: "pending";
        sent_at: string | null;
        scheduled_at: string | null;
        automation_trigger?: string | null;
        metadata?: Record<string, unknown> | null;
      };

      type ReminderSchedulingResult =
        | { status: "opt_out" }
        | { status: "no_contact" }
        | { status: "scheduled"; message: ScheduledMessage };

      if (!organizationId) throw new Error("No organization selected");

      const personIds = input.roster.map((entry) => entry.person.id);
      const preferenceRows = await getGroupReminderPreferences(organizationId, personIds);
      const preferences = new Map(preferenceRows.map((row) => [row.person_id, row]));
      const scheduledAt = resolveReminderScheduleTime(input.meeting);
      const meetingDateLabel = formatGroupMeetingDate(input.meeting);
      const meetingTimeLabel = formatGroupMeetingTime(input.meeting);
      const subject = `Reminder: ${input.group.name} meets ${meetingDateLabel}`;

      const results: ReminderSchedulingResult[] = input.roster.map((entry) => {
        const entryPreferences = preferences.get(entry.person.id);
        if (!isGroupReminderEnabled(entryPreferences, input.channel)) {
          return { status: "opt_out" as const };
        }

        // Codex round-3 finding: useGroupRoster() now returns privacy: null
        // for a member whose directory_profiles row is hidden by the
        // directory-disablement RLS policy, but every person has a row (see
        // 20260627231000_w6_5_directory_share_defaults_fail_closed.sql), so
        // a null here means "hidden," not "no preference set." Falling back
        // to true let a group leader text a member whose actual saved
        // share_phone is false. Fail closed: only send SMS when the roster
        // returned an explicit true.
        const canPhone = entry.privacy?.share_phone === true;
        const address = input.channel === "email" ? entry.person.email : entry.person.phone;
        if (input.channel === "email" && !address) {
          return { status: "no_contact" as const };
        }
        if (input.channel === "sms" && (!canPhone || !address)) {
          return { status: "no_contact" as const };
        }

        const timeLine = meetingTimeLabel ? ` at ${meetingTimeLabel}` : "";
        const locationLine = input.meeting.location ? `Location: ${input.meeting.location}\n\n` : "";
        const body = input.channel === "sms"
          ? `${input.group.name} meets ${meetingDateLabel}${timeLine}. ${input.meeting.location ?? "Reply for details."}`
          : `Hi ${entry.person.first_name},\n\nJust a reminder that ${input.group.name} meets on ${meetingDateLabel}${timeLine}.\n\n${locationLine}See you soon!\n\n-${input.group.name} leaders`;

        return {
          status: "scheduled" as const,
          message: {
            organization_id: organizationId,
            sender_id: input.senderId,
            recipient_person_id: entry.person.id,
            recipient_request_id: null,
            channel: input.channel,
            direction: "outbound",
            recipient_address: address,
            subject: input.channel === "email" ? subject : null,
            body,
            status: "pending",
            sent_at: null,
            scheduled_at: scheduledAt.toISOString(),
            automation_trigger: "group.meeting_reminder",
            metadata: {
              group_id: input.group.id,
              meeting_id: input.meeting.id,
              meeting_date: input.meeting.meeting_date,
            },
          } satisfies ScheduledMessage,
        };
      });

      const scheduledMessages = results.flatMap((result) =>
        result.status === "scheduled" ? [result.message] : []
      );

      if (scheduledMessages.length === 0) {
        return {
          scheduled: 0,
          skippedOptOut: results.filter((result) => result.status === "opt_out").length,
          skippedNoContact: results.filter((result) => result.status === "no_contact").length,
        };
      }

      const { error } = await supabase
        .from("messages")
        .insert(scheduledMessages);

      if (error) throw error;

      return {
        scheduled: scheduledMessages.length,
        skippedOptOut: results.filter((result) => result.status === "opt_out").length,
        skippedNoContact: results.filter((result) => result.status === "no_contact").length,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["messages", organizationId] });
      if (result.scheduled === 0) {
        toast.info("No eligible recipients for this reminder.", {
          description: result.skippedOptOut
            ? `${result.skippedOptOut} opted out, ${result.skippedNoContact} missing contact details.`
            : `${result.skippedNoContact} missing contact details.`,
        });
        return;
      }
      toast.success("Group meeting reminder scheduled", {
        description: `${result.scheduled} reminder${result.scheduled === 1 ? "" : "s"} queued.`,
      });
    },
    onError: (error) => {
      console.error("Failed to schedule reminders:", error);
      toast.error("Failed to schedule reminders");
    },
  });
}

export function useMemberRegistrations(personIds: string[], options?: { eventId?: string }) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-registrations", organizationId, personIds, options?.eventId],
    queryFn: async () => {
      if (!organizationId || personIds.length === 0) return [];

      let query = supabase
        .from("event_registrations")
        .select(`
          id,
          organization_id,
          event_id,
          person_id,
          status,
          payment_status,
          contribution_id,
          notes,
          created_at,
          updated_at,
          event:events(
            id,
            title,
            start_time,
            end_time,
            all_day,
            all_day_date,
            all_day_end_date,
            location,
            registration_enabled,
            registration_capacity,
            registration_fee
          ),
          person:people(
            id,
            first_name,
            last_name,
            email
          )
        `)
        .eq("organization_id", organizationId)
        .in("person_id", personIds)
        .order("created_at", { ascending: false });

      if (options?.eventId) {
        query = query.eq("event_id", options.eventId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []).map(d => ({ ...d, answers: null })) as MemberEventRegistration[];
    },
    enabled: !!organizationId && personIds.length > 0,
  });
}

export function useEventRegistrationStats(eventId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["event-registration-stats", organizationId, eventId],
    queryFn: async () => {
      if (!organizationId || !eventId) return { going: 0, waitlist: 0 };

      const { data, error } = await supabase
        .from("event_registrations")
        .select("status")
        .eq("organization_id", organizationId)
        .eq("event_id", eventId);

      if (error) throw error;

      const going = (data || []).filter((row) => row.status === "going").length;
      const waitlist = (data || []).filter((row) => row.status === "waitlist").length;
      return { going, waitlist };
    },
    enabled: !!organizationId && !!eventId,
  });
}

export function useCreateMemberRegistrations() {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      registrations: Array<{
        person_id: string;
        status: string;
        payment_status: string;
        notes?: string | null;
        answers?: Record<string, unknown> | null;
      }>;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      const payload = input.registrations.map((registration) => ({
        organization_id: organizationId,
        event_id: input.eventId,
        person_id: registration.person_id,
        status: registration.status,
        payment_status: registration.payment_status,
        notes: registration.notes ?? null,
        answers: (registration.answers ?? null) as EventRegistrationInsert["answers"],
      }));

      const { data, error } = await supabase
        .from("event_registrations")
        .insert(payload)
        .select();

      if (error) throw error;

      const { data: event, error: eventError } = await supabase
        .from("events")
        .select("id, title")
        .eq("organization_id", organizationId)
        .eq("id", input.eventId)
        .maybeSingle();

      if (eventError) {
        console.error("Failed to load event for member registration follow-up:", eventError);
      }

      if (user?.id) {
        const personIds = input.registrations.map((registration) => registration.person_id);
        const { data: people, error: peopleError } = await supabase
          .from("people")
          .select("id, first_name, last_name")
          .eq("organization_id", organizationId)
          .in("id", personIds);

        if (peopleError) throw peopleError;

        const nameLookup = new Map(
          (people || []).map((person) => [person.id, `${person.first_name} ${person.last_name}`.trim()])
        );

        const registrationLines = input.registrations.map((registration) => {
          const name = nameLookup.get(registration.person_id) ?? "Member";
          const statusLabel = registration.status === "waitlist" ? "waitlisted" : "registered";
          return `• ${name} (${statusLabel})`;
        });

        const requestDescription = [
          event?.title ? `Event: ${event.title}` : "Event registration received.",
          "Registrations:",
          ...registrationLines,
        ].join("\n");

        const eventRequestPayload = buildCanonicalRequestInsert({
          organizationId,
          triageOwnerId: user.id,
          summary: `New event registration${event?.title ? `: ${event.title}` : ""}`,
          category: "other",
          details: requestDescription,
          priority: "normal",
          visibility: "normal",
        });

        const eventRequestResult = await supabase
          .from("requests")
          .insert(eventRequestPayload as TablesInsert<"requests">)
          .select()
          .single();

        if (eventRequestResult.error && !isSupabaseUnavailableError(eventRequestResult.error)) {
          throw eventRequestResult.error;
        }
      }

      const notifications: NotificationInsert[] = (data ?? []).map((registration) => {
        const isWaitlist = registration.status === "waitlist";
        return {
          organization_id: organizationId,
          person_id: registration.person_id,
          title: isWaitlist ? "Waitlist confirmed" : "Registration confirmed",
          body: event?.title
            ? isWaitlist
              ? `You're on the waitlist for ${event.title}. We'll notify you if a spot opens.`
              : `You're registered for ${event.title}.`
            : isWaitlist
              ? "You're on the waitlist for the event."
              : "You're registered for the event.",
          category: "events",
          link_url: `/member/events/${input.eventId}`,
          link_label: "View event",
        };
      });

      if (notifications.length > 0) {
        const { error: notifyError } = await supabase
          .from("notifications")
          .insert(notifications);

        if (notifyError) {
          console.error("Failed to notify registrants:", notifyError);
        }
      }

      return data as MemberEventRegistration[];
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["member-registrations"] });
      queryClient.invalidateQueries({ queryKey: ["event-registrations", variables.eventId] });
      toast.success("Registration saved");
    },
    onError: (error) => {
      console.error("Failed to register for event:", error);
      toast.error("Failed to register for event");
    },
  });
}

export function useCancelMemberRegistration() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { registrationId: string; eventId: string }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { error } = await supabase
        .from("event_registrations")
        .update({ status: "declined" })
        .eq("id", input.registrationId)
        .eq("organization_id", organizationId);

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["member-registrations"] });
      queryClient.invalidateQueries({ queryKey: ["event-registrations", variables.eventId] });
      // Moving a registration from going to declined can promote the next
      // waitlisted person in the database trigger. Refresh the staff-facing
      // waitlist caches too, so they never act on the stale pre-promotion row.
      queryClient.invalidateQueries({ queryKey: ["waitlist-entries", variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ["events-with-waitlist", organizationId] });
      toast.success("Registration cancelled");
    },
    onError: (error) => {
      console.error("Failed to cancel registration:", error);
      toast.error("Failed to cancel registration");
    },
  });
}

export function useMemberAttendance(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-attendance", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId) return [];

      const { data, error } = await supabase
        .from("attendance")
        .select(`
          id,
          organization_id,
          person_id,
          event_id,
          event_name,
          event_date,
          check_in_method,
          check_in_time,
          notes,
          event:events(
            id,
            title,
            start_time,
            location
          )
        `)
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("event_date", { ascending: false })
        .order("check_in_time", { ascending: false });

      if (error) throw error;
      return (data || []) as MemberAttendanceRecord[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberCheckinTargets() {
  const { organizationId } = useOrganization();
  const { settings: orgSettings } = useOrgSettings();
  // A member's self-check-in surface can stay mounted across the
  // organization's midnight; reading `new Date()` alone doesn't itself
  // schedule a refetch, so the query could keep returning yesterday's
  // covered day (and a member could submit against a memoized stale
  // target date) until something else happened to refetch it. Including
  // the org-midnight tick in the query key forces a refetch at that exact
  // boundary (Codex round-33 finding on #5782).
  const orgNow = useOrgMidnightTick(orgSettings.timezone);

  return useQuery({
    queryKey: ["member-checkin-targets", organizationId, orgSettings.timezone, orgNow],
    queryFn: async () => {
      if (!organizationId) return { events: [], services: [] };

      // "Today" for a member's self-check-in must be the organization's own
      // calendar day, not whatever day the member's own browser timezone
      // happens to read — otherwise an all-day event anchored to the
      // organization's midnight is absent from the list entirely for a
      // member outside that timezone (Codex round-9 finding on #5782).
      const dateKey = timeZoneDateYmd(orgSettings.timezone, orgNow);
      const todayStartIso = zonedMidnightToUtcIso(dateKey, orgSettings.timezone) ?? new Date().toISOString();
      const todayEndIso = zonedEndOfDayToUtcIso(dateKey, orgSettings.timezone) ?? new Date().toISOString();
      // Padded by a day on each side — an all-day event's start_time is
      // anchored to organization midnight, which can fall just outside
      // today's naive boundary depending on the organization's timezone
      // offset relative to UTC. The precise re-filter below applies the
      // exact boundary afterward.
      const queryStart = new Date(new Date(todayStartIso).getTime() - 24 * 60 * 60 * 1000);
      const queryEnd = new Date(new Date(todayEndIso).getTime() + 24 * 60 * 60 * 1000);

      // Interval-overlap, not a start-time-only bound — a multi-day
      // all-day event can start well before "today" and still be
      // underway; bounding only by start_time would drop it from the
      // fetch itself before the re-filter below ever runs (Codex round-10
      // finding on #5782, same overlap fix as GeneralCheckIn.tsx and
      // useEvents.tsx).
      const { data: rawEvents, error: eventsError } = await supabase
        .from("events")
        .select("id, title, start_time, end_time, location, all_day, all_day_date, all_day_end_date")
        .eq("organization_id", organizationId)
        .or(`and(end_time.gte.${queryStart.toISOString()},start_time.lte.${queryEnd.toISOString()}),and(all_day.eq.true,all_day_date.lte.${dateKey},all_day_end_date.gte.${dateKey})`)
        .order("start_time", { ascending: true });

      if (eventsError) throw eventsError;

      const events = (rawEvents || []).filter((event) => {
        if (event.all_day) {
          const eventStartDate = parseEventDateTime(event.start_time);
          if (!eventStartDate) return false;
          const eventEndDate = parseEventDateTime(event.end_time);
          const startDateStr = (event.all_day_date ?? timeZoneDateYmd(orgSettings.timezone, eventStartDate));
          const endDateStr = eventEndDate
            ? (event.all_day_end_date ?? timeZoneDateYmd(orgSettings.timezone, eventEndDate))
            : startDateStr;
          return dateKey >= startDateStr && dateKey <= endDateStr;
        }
        const eventMs = new Date(event.start_time).getTime();
        return eventMs >= new Date(todayStartIso).getTime() && eventMs <= new Date(todayEndIso).getTime();
      });

      const { data: services, error: servicesError } = await supabase
        .from("services")
        .select("id, title, scheduled_date, start_time, end_time, location")
        .eq("organization_id", organizationId)
        .eq("scheduled_date", dateKey)
        .order("start_time", { ascending: true });

      if (servicesError) throw servicesError;

      return {
        events,
        services: services || [],
      };
    },
    enabled: !!organizationId,
  });
}

export function useMemberCheckIn() {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      personId: string;
      eventId?: string | null;
      eventName: string;
      eventDate: string;
      notes?: string | null;
      method?: string;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: priorAttendance, error: priorError } = await supabase
        .from("attendance")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("person_id", input.personId)
        .limit(1);

      if (priorError) throw priorError;

      const isFirstTime = !priorAttendance || priorAttendance.length === 0;
      const defaultNote = isFirstTime ? "First-time check-in via member portal." : "Returning check-in via member portal.";

      const existingQuery = supabase
        .from("attendance")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("person_id", input.personId)
        .eq("event_date", input.eventDate);

      const { data: existing, error: existingError } = input.eventId
        ? await existingQuery.eq("event_id", input.eventId).maybeSingle()
        : await existingQuery
            .is("event_id", null)
            .eq("event_name", input.eventName)
            .maybeSingle();

      if (existingError) throw existingError;
      if (existing) {
        return { record: existing, isFirstTime, alreadyCheckedIn: true };
      }

      const { data, error } = await supabase
        .from("attendance")
        .insert({
          organization_id: organizationId,
          person_id: input.personId,
          event_id: input.eventId ?? null,
          event_name: input.eventName,
          event_date: input.eventDate,
          check_in_method: input.method ?? "app",
          check_in_time: new Date().toISOString(),
          notes: input.notes ?? defaultNote,
          is_first_visit: isFirstTime,
        })
        .select()
        .single();

      if (error) throw error;
      return { record: data, isFirstTime, alreadyCheckedIn: false };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["member-attendance"] });
      if (result.alreadyCheckedIn) {
        toast.message("You're all set", {
          description: "You're already checked in for this time slot.",
        });
        return;
      }
      toast.success("You're all set", {
        description: result.isFirstTime
          ? "Welcome! Your first check-in is recorded."
          : "Check-in confirmed.",
      });
    },
    onError: (error) => {
      console.error("Failed to check in:", error);
      toast.error("Check-in failed. Please try again or contact an admin.");
    },
  });
}

export function useMemberPledges(personId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-pledges", organizationId, personId],
    queryFn: async () => {
      if (!organizationId || !personId || isMemberPledgesUnavailable(organizationId)) return [];

      const { data, error } = await supabase
        .from("member_pledge_progress")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .eq("status", "active")
        .order("start_date", { ascending: false });

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markMemberPledgesUnavailable(organizationId);
          return [];
        }
        throw error;
      }
      return (data || []) as MemberPledgeProgress[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberPledgeCampaigns() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["member-pledge-campaigns", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from("campaigns")
        .select("id, organization_id, name, status, start_date, end_date, fund_id")
        .eq("organization_id", organizationId)
        .in("status", ["active", "upcoming"])
        .order("start_date", { ascending: true });

      if (error) throw error;
      return (data || []) as MemberPledgeCampaign[];
    },
    enabled: !!organizationId,
  });
}

export function useMemberNextUp(personId: string | undefined) {
  const { organizationId } = useOrganization();
  const { settings: orgSettings } = useOrgSettings();

  return useQuery({
    queryKey: ["member-next-up", organizationId, personId, orgSettings.timezone],
    queryFn: async () => {
      if (!organizationId || !personId) return null;

      const now = new Date();
      const nowIso = now.toISOString();
      const nowMs = now.getTime();
      const todayKey = localDateYmd();
      const orgTodayKey = timeZoneDateYmd(orgSettings.timezone, now);
      // An all-day event's own start_time is anchored to organization
      // midnight, so a plain `start_time >= now` bound (correct for a
      // timed event) would drop the event from this query the instant
      // "now" passes midnight — removing a registered all-day event from
      // the Next up card for the rest of the day it's actually happening
      // on. Bound by end_time instead so an all-day (or multi-day) event
      // stays eligible through its full organization-calendar interval,
      // and let the client-side filter below apply the precise per-type
      // rule (Codex round-10 finding on #5782).
      const { data: registrations, error: registrationError } = await supabase
        .from("event_registrations")
        .select("id, event:events!inner(id, title, start_time, end_time, all_day, all_day_date, all_day_end_date, location)")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .or(`end_time.gte.${nowIso},and(all_day.eq.true,all_day_end_date.gte.${orgTodayKey})`, { referencedTable: "event" })
        .order("start_time", { foreignTable: "events", ascending: true })
        .order("id", { ascending: true });

      if (registrationError) throw registrationError;

      type NextUpRegistration = {
        id?: string;
        event?: {
          start_time?: string | null;
          end_time?: string | null;
          all_day_date?: string | null;
          all_day_end_date?: string | null;
          all_day?: boolean | null;
        } | null;
      };

      const eventRegistration = (registrations || [])
        .filter((registration: NextUpRegistration) => {
          const event = registration.event;
          if (!event?.start_time) return false;
          if (event.all_day) return !event.all_day_end_date || event.all_day_end_date >= orgTodayKey;
          // Compare as instants, not raw strings — PostgREST can return a
          // timestamp in "+00:00" offset form, which is not
          // lexicographically equivalent to the "Z"-suffixed boundary even
          // though the two represent the same instant.
          return new Date(event.start_time).getTime() >= nowMs;
        })
        .sort((a: NextUpRegistration, b: NextUpRegistration) => {
          const aMs = eventStartMillis(a.event ?? {}, orgSettings.timezone);
          const bMs = eventStartMillis(b.event ?? {}, orgSettings.timezone);
          if (aMs !== bMs) return aMs - bMs;
          return (a.id ?? "").localeCompare(b.id ?? "");
        })[0];
      const eventStart = eventRegistration?.event?.start_time ?? null;
      const eventAllDay = eventRegistration?.event?.all_day ?? false;

      const { data: assignments, error: assignmentError } = await supabase
        .from("volunteer_assignments")
        .select("id, service:services!inner(id, title, scheduled_date, start_time, location)")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .gte("service.scheduled_date", todayKey)
        .order("scheduled_date", { foreignTable: "services", ascending: true })
        .order("start_time", { foreignTable: "services", ascending: true })
        .order("id", { ascending: true });

      if (assignmentError) throw assignmentError;

      const assignment = ((assignments || []) as MemberServingAssignment[])
        .filter((item) => {
          const assignmentStart = serviceStartIso(item.service);
          return assignmentStart ? assignmentStart >= nowIso : false;
        })
        .sort(compareServingAssignments)[0];
      const assignmentStart = serviceStartIso(assignment?.service);

      const candidates: MemberNextUpItem[] = [];

      if (
        eventRegistration?.event?.start_time &&
        (eventAllDay || new Date(eventRegistration.event.start_time).getTime() >= nowMs)
      ) {
        candidates.push({
          type: "event",
          title: eventRegistration.event.title,
          startTime: eventRegistration.event.start_time,
          endTime: eventRegistration.event.end_time,
          allDay: eventAllDay,
          allDayDate: eventRegistration.event.all_day_date,
          allDayEndDate: eventRegistration.event.all_day_end_date,
          location: eventRegistration.event.location,
          href: `/calendar/${eventRegistration.event.id}`,
        });
      }

      if (assignment?.service && assignmentStart && assignmentStart >= nowIso) {
        candidates.push({
          type: "volunteer",
          title: assignment.service.title,
          startTime: assignmentStart,
          location: assignment.service.location,
          href: `/services/${assignment.service.id}`,
        });
      }

      if (candidates.length === 0) return null;

      const candidateStart = (item: MemberNextUpItem) => eventStartMillis({
        start_time: item.startTime, all_day: item.allDay, all_day_date: item.allDayDate,
      }, orgSettings.timezone);
      return candidates.sort((a, b) => candidateStart(a) - candidateStart(b))[0];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useMemberServingSchedule(personId: string | undefined, options?: { limit?: number }) {
  const { organizationId } = useOrganization();
  const limit = options?.limit ?? 4;

  return useQuery({
    queryKey: ["member-serving-schedule", organizationId, personId, limit],
    queryFn: async () => {
      if (!organizationId || !personId) return [] as MemberServingAssignment[];

      const todayKey = localDateYmd();

      const { data, error } = await supabase
        .from("volunteer_assignments")
        .select(`
          id,
          status,
          position:service_positions(title),
          service:services!inner(id, title, scheduled_date, start_time, end_time, location)
        `)
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .order("scheduled_date", { foreignTable: "services", ascending: true });

      if (error) throw error;

      const upcoming = ((data || []) as MemberServingAssignment[])
        .filter((assignment) =>
          assignment.service?.scheduled_date
            ? assignment.service.scheduled_date >= todayKey
            : false
        )
        .sort(compareServingAssignments);
      return (limit > 0 ? upcoming.slice(0, limit) : upcoming) as MemberServingAssignment[];
    },
    enabled: !!organizationId && !!personId,
  });
}

export function useUpdateMemberProfile() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ personId, updates }: { personId: string; updates: { first_name?: string; last_name?: string; email?: string | null; phone?: string | null; photo_url?: string | null; communication_pref?: string } }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("people")
        .select("*")
        .eq("id", personId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("people")
        .update(updates)
        .eq("id", personId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;

      await logAudit({
        tableName: "people",
        recordId: personId,
        action: "update_member_profile",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["person", data.id] });
      queryClient.invalidateQueries({ queryKey: ["current-person"] });
      toast.success("Profile updated");
    },
    onError: (error) => {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    },
  });
}

export function useUpdateHouseholdContact() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ householdId, updates }: { householdId: string; updates: { address?: string | null; city?: string | null; state?: string | null; zip?: string | null; phone?: string | null } }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("households")
        .select("*")
        .eq("id", householdId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("households")
        .update(updates)
        .eq("id", householdId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;

      await logAudit({
        tableName: "households",
        recordId: householdId,
        action: "update_household_contact",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.id] });
      toast.success("Household updated");
    },
    onError: (error) => {
      console.error("Failed to update household:", error);
      toast.error("Failed to update household");
    },
  });
}

export function useUpdateDependentProfile() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({ personId, updates }: { personId: string; updates: { first_name?: string; last_name?: string; email?: string | null; phone?: string | null } }) => {
      if (!organizationId) throw new Error("No organization selected");

      const { data: before } = await supabase
        .from("people")
        .select("*")
        .eq("id", personId)
        .eq("organization_id", organizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from("people")
        .update(updates)
        .eq("id", personId)
        .eq("organization_id", organizationId)
        .select()
        .single();

      if (error) throw error;

      await logAudit({
        tableName: "people",
        recordId: personId,
        action: "update_dependent_profile",
        organizationId,
        oldData: (before as Record<string, unknown> | null) ?? null,
        newData: data as Record<string, unknown> | null,
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["household", data.household_id] });
      toast.success("Dependent updated");
    },
    onError: (error) => {
      console.error("Failed to update dependent:", error);
      toast.error("Failed to update dependent");
    },
  });
}
